import type { CustomCellEditorProps } from '@ag-grid-community/react';
import React, { useImperativeHandle, useRef } from 'react';
import {
  BasicConfigurableDataModal,
  type BasicConfigurableDataModalProps,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ConfigurableDataModal/BasicConfigurableDataModal';

// need to omit the value from CustomCellEditorProps because
// BasicConfigurableDataModalProps defines its own value property
interface BasicConfigurableDataModalEditorProps
  extends BasicConfigurableDataModalProps,
    Omit<CustomCellEditorProps, 'value'> {}

/**
 * This is a custom ag-grid editor component.
 *
 * It wraps the core component and setups the necessary ag-grid handlers
 * to allow this component to be used both outside ag-grid (BasicConfigurableDataModal)
 * and inside ag-grid as an editor (BasicConfigurableDataModalEditor).
 * */
export const BasicConfigurableDataModalEditor = React.forwardRef(function BasicConfigurableDataModalEditor(
  props: BasicConfigurableDataModalEditorProps,
  forwardedRef,
) {
  const { node, stopEditing, ...modalProps } = props;
  // ref is required since the localValue will be accessed in a different function scope `useImperativeHandle
  const localValueRef = useRef(props.value);

  const onSubmit = (selection: unknown) => {
    const nextSelection = selection ?? props.value;
    localValueRef.current = nextSelection;
    stopEditing();
  };

  const onCancel = () => {
    localValueRef.current = props.value;
    stopEditing(true);
  };

  useImperativeHandle(forwardedRef, () => {
    return {
      getValue: () => localValueRef.current,
    };
  }, []);

  return (
    <BasicConfigurableDataModal
      {...modalProps}
      value={localValueRef.current}
      parentData={node.data}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
});
